import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core"
import { Router } from "@angular/router"
import { FormBuilder } from "@angular/forms"
import { HttpClient } from "@angular/common/http"
import Swal from "sweetalert2"
import { confirmationAlertSwalSettings } from "@service/app.config"
import {
  AccountService,
  EmailTemplateService,
  ToasterLibraryService,
} from "@service/index"
import { NgxPermissionsService } from "ngx-permissions"
import * as _ from "lodash"

interface TemplateLegendItem {
  template?: string
  legend?: { template: string; name: string }
  name?: string
  id?: number
  description?: string
  prompt?: string
}

interface TemplateLegendCategory {
  [key: string]: TemplateLegendItem[]
}

@Component({
  selector: "app-template-legend",
  templateUrl: "./template-legend-modal.component.html",
  styleUrls: ["./template-legend-modal.component.scss"],
})
export class TemplateLegendModalComponent implements OnInit {
  public activeTab: string = "contact"
  public templateLegends: TemplateLegendCategory = {}
  public selectedLegend: Array<any> = []
  public isAILegendSelected: {
    selected: boolean
    isAction: string
    isPreview: boolean
  } = {
    selected: false,
    isAction: "insert",
    isPreview: false,
  }
  public isLoaded = false
  public isSaveEditAIVar: boolean = false
  public isGeneratingPreview: boolean = false
  public isPreviewEditable: boolean = false
  public myAccount: Array<any> = []

  @Input() view: string = "mini"
  @ViewChild("editor") editorElem!: ElementRef<HTMLTextAreaElement>
  @Output("templateLegendCloseEvt") templateLegendCloseEvt = new EventEmitter()
  @Output("templateActiveTabEvt") templateActiveTabEvt = new EventEmitter()
  @Output("templateLegendInsertEvt") templateLegendInsertEvt =
    new EventEmitter()

  constructor(
    fb: FormBuilder,
    private router: Router,
    private emailService: EmailTemplateService,
    private http: HttpClient,
    private toasterLibrary: ToasterLibraryService,
    private accountService: AccountService,
    private permissionsService: NgxPermissionsService
  ) {}

  public selectedEditLegend: any = {
    name: "",
    template: "",
    description: "",
    tone: "Direct",
    prompt: "",
    preview: "",
  }

  ngOnInit() {
    this.getTemplateLegends()
  }

  getTemplateLegends() {
    this.isLoaded = false
    this.emailService.getTemplateLegend().subscribe(
      (res: any) => {
        if (res.success === "true") {
          this.templateLegends = res.data
          this.loadLegends(this.activeTab)
          this.isLoaded = true
        } else {
          this.isLoaded = true
          this.toasterLibrary.error(null, "Failed to load template legends!")
        }
      },
      err => {
        this.isLoaded = true
        console.error("Error fetching template legends:", err)
        this.toasterLibrary.error(
          null,
          "An error occurred while fetching template legends."
        )
      }
    )
  }

  async loadLegends(type: string) {
    this.isLoaded = false
    this.activeTab = type
    this.setAIVarDefaults()
    // if (this.activeTab === "ai_variables") {
    //   await this.loadExampleLegends()
    // }
    this.selectedLegend = this.templateLegends[type] || []
    // console.log("Selected Legend:", this.selectedLegend)
    this.isLoaded = true
  }

  // async loadExampleLegends() {
  //   this.isLoaded = false
  //   try {
  //     const data: any = await this.http.get(this.aiVariablesUrl).toPromise() // Using toPromise is okay here for simplicity, but consider using async/await with rxjs operators like lastValueFrom or firstValueFrom for better practice
  //     this.templateLegends["ai_variables"] = data || []
  //     console.log("Mock API response:", this.templateLegends)
  //     this.isLoaded = true
  //   } catch (error) {
  //     this.toasterLibrary.error(null, "Error fetching AI Variables")
  //     this.isLoaded = true
  //     console.error("Error loading example legends:", error)
  //   }
  // }

  setAIVarDefaults() {
    this.isAILegendSelected = {
      selected: false,
      isAction: "insert",
      isPreview: false,
    }
    this.selectedEditLegend = {
      id: null,
      name: "",
      description: "",
      tone: "Direct",
      prompt: "",
      preview: "",
    }
    this.isPreviewEditable = false
    this.isSaveEditAIVar = false
    this.isGeneratingPreview = false
  }

  editVariable(id: number) {
    this.isAILegendSelected = {
      selected: true,
      isAction: "edit",
      isPreview: false,
    }
    const legendToEdit = this.selectedLegend.find(legend => legend.id == id)
    if (legendToEdit) {
      this.selectedEditLegend = {
        ...legendToEdit,
        prompt: legendToEdit.prompt || "",
        preview: legendToEdit.preview || "",
      }
    }
  }

  async insertVariable(name: string, insert: boolean) {
    // console.log(this.isAILegendSelected)
    this.isAILegendSelected = {
      selected: true,
      isAction: "insert",
      isPreview: false,
    }
    const legendToInsert = this.selectedLegend.find(
      legend => legend.name == name
    )
    if (legendToInsert) {
      this.selectedEditLegend = {
        ...legendToInsert,
        prompt: legendToInsert.prompt
          ? legendToInsert.prompt.replaceAll(
              "{tone}",
              legendToInsert.tone.toLowerCase()
            )
          : "",
      }

      // console.log(this.selectedEditLegend)
      // console.log(this.isAILegendSelected)
      // console.log(this.isPromptUpdated(this.selectedEditLegend.name))
      // console.log(insert)

      if (insert) {
        const response = await this.generateAIPreview()

        if (response) {
          this.selectedEditLegend.preview = response
          this.insertTemplateLegend(true, this.selectedEditLegend)
        }
      }
    }
  }

  async viewVariable(name: string) {
    this.isAILegendSelected = {
      selected: true,
      isAction: "view",
      isPreview: false,
    }
    const legendToView = this.selectedLegend.find(legend => legend.name == name)
    if (legendToView) {
      this.selectedEditLegend = {
        ...legendToView,
        prompt: legendToView.prompt
          ? legendToView.prompt.replaceAll(
              "{tone}",
              legendToView.tone.toLowerCase()
            )
          : "",
      }
    }
  }

  insertTemplateLegend(
    status: boolean,
    ai_legend_template: any = null,
    default_legend: string = null
  ) {
    // console.log("Emitting template legend:", {
    //   status,
    //   default_legend,
    //   ai_legend_template,
    // })
    this.templateLegendInsertEvt.emit({
      status: status,
      default_legend: default_legend,
      ai_legend_template: ai_legend_template,
    })
  }

  copyVariable(legend: any) {
    const template =
      this.activeTab === "contact" ||
      this.activeTab === "sender" ||
      this.activeTab === "ai_variables"
        ? legend.template
        : legend.legend?.template

    if (template) {
      navigator.clipboard.writeText(template).then(
        () => {
          this.toasterLibrary.success(null, "Template copied to clipboard!")
        },
        err => {
          console.error("Could not copy text: ", err)
          this.toasterLibrary.error(null, "Failed to copy to clipboard")
        }
      )
    }
  }

  updateAIVar() {
    if (!this.selectedEditLegend.id) {
      this.toasterLibrary.error(null, "No ID provided for update.")
      return
    }
    this.isSaveEditAIVar = true
    let name = this.selectedEditLegend.name.replace(/ +/g, "_")

    if (!name.startsWith("{")) {
      name = "{" + name
    }

    if (!name.endsWith("}")) {
      name = name + "}"
    }

    this.selectedEditLegend.name = name

    // this.http
    //   .put(
    //     `${this.aiVariablesUrl}/${this.selectedEditLegend.id}`,
    //     this.selectedEditLegend
    //   )
    //   .subscribe(
    //     (response: any) => {
    //       this.toasterLibrary.success(null, "AI Variable Updated Successfully!")
    //       this.isSaveEditAIVar = false
    //       this.isPreviewEditable = false
    //       this.loadLegends("ai_variables")
    //     },
    //     error => {
    //       this.isSaveEditAIVar = false
    //       this.isPreviewEditable = false
    //       this.toasterLibrary.error(null, "Error updating AI Variable")
    //       console.error("Error updating AI Variable:", error)
    //     }
    //   )
  }

  async generateAIPreview() {
    this.isSaveEditAIVar = true
    this.isGeneratingPreview = true

    if (this.selectedEditLegend.prompt) {
      try {
        const res: any = await this.accountService
          .getOpenAISettings()
          .toPromise()
        const openAiKey = res?.data

        if (!openAiKey || openAiKey == "") {
          this.toasterLibrary.error(
            "No Openai AP Key Found!",
            "Please add openai key in general setting! Redirecting..."
          )
          this.router.navigate(["/settings/general"])
          return null
        }

        let promptText = this.selectedEditLegend.prompt
        const templateVars = promptText.match(/{([^}]+)}/g)

        if (templateVars) {
          templateVars.forEach(variable => {
            const varName = variable.trim()
            const contactVar = this.templateLegends["contact"]?.find(
              v => v.template === varName
            )
            const rolesVar = this.templateLegends["roles"]?.find(
              v => v.legend.template === varName
            )
            const senderVar = this.templateLegends["sender"]?.find(
              v => v.template === varName
            )
            const fieldsVar = this.templateLegends["fields"]?.find(
              v => v.legend.template === varName
            )
          })
        }

        this.selectedEditLegend.prompt = promptText

        // Wait for getMyAccount to complete before proceeding
        await this.getMyAccount()

        const response = await this.emailService
          .generatePreview(this.selectedEditLegend, openAiKey, this.myAccount)
          .toPromise()

        if (response) {
          this.toasterLibrary.success(null, "Preview generated successfully!")
          // console.log(response)
        }
        return response
      } catch (error) {
        this.toasterLibrary.error(null, "Error generating preview:" + error)
        return null
      } finally {
        this.isSaveEditAIVar = false
        this.isAILegendSelected = {
          selected: !this.isAILegendSelected.selected,
          isAction: this.isAILegendSelected.isAction,
          isPreview: false,
        }
        this.isGeneratingPreview = false
      }
    } else {
      this.isSaveEditAIVar = false
      this.selectedEditLegend.preview = ""
      this.isGeneratingPreview = false
      this.toasterLibrary.error(
        null,
        "Please enter a prompt to generate a preview."
      )
      return null
    }
  }

  async getMyAccount() {
    const res: any = await this.accountService.getMyAccount().toPromise()

    if (res.success == "true") {
      this.myAccount = res.data
    } else {
      this.toasterLibrary.error(null, res.message)
    }
  }

  isPromptUpdated(aiVarName: string | null): boolean {
    const aiVariable = Array.isArray(this.templateLegends["ai_variables"])
      ? this.templateLegends["ai_variables"].find(
          legend => legend.name == aiVarName
        )
      : undefined

    return (
      !!aiVariable &&
      aiVariable.prompt !== this.selectedEditLegend.prompt &&
      this.selectedEditLegend.prompt !== ""
    )
  }

  togglePreviewEdit() {
    this.isPreviewEditable = !this.isPreviewEditable
  }

  addAIVar() {
    this.isSaveEditAIVar = true
    let template = this.selectedEditLegend.name
      .toLowerCase()
      .replace(/ +/g, "_")

    if (!template.startsWith("{ai_var_")) {
      template = "{ai_var_" + template
    }

    // console.log(this.selectedLegend)
    // console.log(this.templateLegends["ai_variables"])

    var counter = 1
    let isTemplateIncluded = _.includes(
      _.map(this.selectedLegend, "template"),
      template + "}"
    )

    let base_template = template
    while (isTemplateIncluded) {
      template = base_template + "_" + counter++
      isTemplateIncluded = _.includes(
        _.map(this.selectedLegend, "template"),
        template + "}"
      )
    }

    if (!template.endsWith("}")) {
      template = template + "}"
    }

    this.selectedEditLegend.template = template

    const payload = { ...this.selectedEditLegend }
    delete payload.id
    delete payload.preview

    this.emailService.postTemplateLegend(payload).subscribe(
      (response: any) => {
        this.toasterLibrary.success(null, "Added AI Var Successfully!")
        this.isSaveEditAIVar = false
        this.isPreviewEditable = false
        this.getTemplateLegends()
      },
      error => {
        this.isSaveEditAIVar = false
        this.isPreviewEditable = false
        this.toasterLibrary.error(null, "Error adding AI Variable")
        console.error("Error adding AI Variable:", error)
      }
    )
  }

  showAIVariableModal() {
    this.isAILegendSelected = {
      selected: true,
      isAction: "add",
      isPreview: false,
    }
    this.selectedEditLegend = {
      id: null,
      name: "",
      description: "",
      tone: "Direct",
      prompt: "",
      preview: "",
    }
  }

  deleteVariable(id: number) {
    if (!id) {
      this.toasterLibrary.error(null, "No ID provided for deletion.")
      return
    }

    let swal_opts = Object.assign({}, confirmationAlertSwalSettings)

    swal_opts[
      "html"
    ] = `<span class='confirm-alert-body'>Are You sure you want to Delete this AI variable?</span>`

    Swal.fire(swal_opts).then(result => {
      if (result.isConfirmed) {
        this.isLoaded = false
        this.emailService.deleteTemplateLegend(id).subscribe(
          (response: any) => {
            this.toasterLibrary.success(
              null,
              `Deleted AI Variable Successfully!`
            )
            this.isLoaded = true
            this.getTemplateLegends()
          },
          error => {
            this.isLoaded = true
            this.toasterLibrary.error(null, "Error deleting AI Variable")
            console.error("Error deleting AI Variable:", error)
          }
        )
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        this.isLoaded = true
      }
    })
  }


  /**
   * AI Variables write actions: templatelegends post/delete are often missing from
   * org/group access (only can_get is granted). Treat email-template editors as
   * able to manage AI vars; API authorize is aligned the same way.
   */
  canManageAiVariables(): boolean {
    const perms = this.permissionsService.getPermissions() || {}
    return !!(
      perms["templatelegends:can_post_templatelegends"] ||
      perms["templatelegends:can_delete_templatelegends"] ||
      perms["emailtemplates:can_post_emailtemplates"]
    )
  }

  truncateDescription(desc: string, maxLength: number = 60): string {
    return desc && desc.length > maxLength
      ? desc.substring(0, maxLength) + "..."
      : desc
  }

  onTemplateChange = $event => {
    // console.log($event)
  }

  closeDialog() {
    // this.templateLegendCloseEvt.emit(true)
  }

  cancelDialog() {
    // this.templateLegendCloseEvt.emit(false)
  }
}
